import HBButton from "@/components/HBButton";
import HBPageTitle from "@/components/HBPageTitle";
import { getUserPins } from "@/services";
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import useRequest from "@/utils/useReq";

import {
  useMemoizedFn,
  useSafeState,
  useSetState,
  useUpdateEffect,
} from "ahooks";

import { checkCanUploadPin } from "@/utils/open";
import useUrlState from "@/utils/useUrlState";
import { last, omit } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import HBIcon from "../../HBIcon";
import HBMessage from "../../HBMessage";
import PinList from "../../PinList";
import styles from "../index.module.less";
import Empty from "./Empty";
import Loading from "./Loading";
import TabPanel from "./TabPanel";

export default function PinListPanel({
  user,
  userStore,
  isMe,
  initPins,
  showTitle = false,
  showCreated = false,
  hideCreateBtn = false,
  created = false,
}) {
  const [records, setRecords] = useSafeState(initPins || []);
  const [hasMore, setHasMore] = useSafeState(true);
  const [loading, setLoading] = useSafeState(!initPins);
  const [filter, setQuery] = useUrlState();

  const [filterQuery, setFilterQuery] = useSetState({
    max: initPins ? initPins[initPins.length - 1] : null,
    page: 1,
    per_page: 20,
  });
  const initRef = useRef(!initPins);

  const router = useRouter();
  const { currentTab } = router.query;

  // 我创建的 切换时，重置采集列表
  useEffect(() => {
    setRecords([]);
    setFilterQuery({
      page: 1,
      max: null,
    });
  }, [created, setFilterQuery, setRecords]);

  // 瀑布流加载
  const loadData = useMemoizedFn(() => {
    setFilterQuery({
      page: filterQuery.page + 1,
      max: last(records),
    });
  });

  // 获取用户采集
  useRequest(
    async () => {
      if (!initRef.current) {
        // 如果服务端渲染进来之后初次不用执行
        initRef.current = true;
        return;
      }
      setLoading(true);
      const PAGE_SIZE = 30;
      let params;
      const { type, time } = filter;
      const [date_since, date_until] = time?.split("-") || [];

      if (type || created) {
        // 有类型筛选条件的， 走搜索参数
        params = {
          username: user.urlname,
          sort: "created_at", // 默认按创建时间降序排序
          ...omit(filterQuery, ["max"]),
          material: type === "material",
          ...(type !== "material" ? { category: type } : null),
        };

        // 我创建的
        if (created) {
          params.created = true;
        } else {
          delete params.created;
        }
      } else {
        params = {
          username: user.urlname,
          date_since: Number(date_since) || undefined,
          date_until: Number(date_until) || undefined,
          max: filterQuery.max?.pin_id,
          limit: PAGE_SIZE,
        };
      }

      const { err, pins } = await getUserPins(params);
      setLoading(false);
      if (err) return;

      if (pins.length <= 0) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      if (filterQuery.max || filterQuery.page > 1) {
        pvTracker(getPageData());
      }

      setRecords((records) =>
        filterQuery.max ? [...records.concat(pins)] : pins
      );
    },
    { refreshDeps: [filterQuery, filter?.time, filter?.type] }
  );

  // 切换用户时，重置采集列表
  useUpdateEffect(() => {
    setRecords([]);
    setHasMore(true);
    setFilterQuery({
      page: 1,
      max: null,
    });
  }, [user?.user_id]);

  // 点击创建采集，显示图片上传弹窗
  const onCreateClick = useCallback(async () => {
    if (!checkCanUploadPin(user)) {
      return;
    }
    const { PinUploaderModal } = await import("@/components/PinCreator");

    PinUploaderModal.open({
      name: "pinUploaderModal",
      onOk: async () => {
        HBMessage.success("添加成功");
        setRecords([]);
        setFilterQuery({
          page: 1,
          max: null,
        });
      },
    });
  }, [user, setFilterQuery, setRecords]);

  // 瀑布流第一个位置的，创建采集卡片
  const createBtn = useMemo(
    () => (
      <div
        className={styles.createRect}
        key={"createBtn"}
        onClick={onCreateClick}
        data-gd-click="button_click"
        data-button-name="添加采集"
        title="上传采集"
      >
        <div className={styles.block}>
          <HBIcon type={"plus"} className={styles.icon} />
        </div>
      </div>
    ),
    [onCreateClick]
  );

  // 显示 创建的 按钮
  const actions = useMemo(() => {
    if (!showCreated) return null; // 不显示创建的按钮
    return (
      <div className={styles.actionsContent}>
        <HBButton
          className={styles.createdBtn}
          type={currentTab === "pins" ? "primary" : "default"}
          size="small"
        >
          <Link href="/space/pins">
            <a data-gd-click="button_click" data-button-name="全部灵感">
              {" "}
              全部{" "}
            </a>
          </Link>
        </HBButton>
        <HBButton
          className={styles.createdBtn}
          type={currentTab === "created" ? "primary" : "default"}
          size="small"
        >
          <Link href="/space/created">
            <a data-gd-click="button_click" data-button-name="我创建的">
              {" "}
              我创建的{" "}
            </a>
          </Link>
        </HBButton>

        <HBButton className={styles.tagsBtn} size="small">
          <Link href="/space/tags">
            <a data-gd-click="button_click" data-button-name="标签">
              {" "}
              标签{" "}
            </a>
          </Link>
        </HBButton>
      </div>
    );
  }, [showCreated, currentTab]);

  return (
    <>
      {/* 显示标题: showTitle = true */}
      {showTitle && (
        <HBPageTitle
          title={`${isMe ? "我" : user?.username}全部采集的`}
          backAction={() => {
            router.back();
          }}
        />
      )}

      <TabPanel actions={actions}>
        {loading && !records.length ? (
          <Loading height={260} />
        ) : (
          <div style={{ marginTop: "16px" }}>
            {records.length ? (
              <PinList
                dataSource={records}
                onLoadMore={loadData}
                hasMore={hasMore}
                hideUser
                hideCount={!isMe}
              >
                {isMe && !hideCreateBtn ? createBtn : null}
              </PinList>
            ) : isMe && !hideCreateBtn ? (
              <>
                {createBtn}
                <div className={styles.empty}>还没有任何内容～</div>
              </>
            ) : (
              <Empty
                imageUrl={
                  "https://cdn.dancf.com/fe-assets/img/e66686c56220ce789c966b6653936017.png"
                }
                title={`${isMe ? "您" : "Ta"}还没有上传任何内容～`}
              />
            )}
          </div>
        )}
      </TabPanel>
    </>
  );
}
